import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import {
  Check, ChevronRight, TrendingUp, BarChart3, PieChart,
  Target, Brain, AlertTriangle, CheckCircle2
} from "lucide-react";
import SEO from "../../components/SEO";

const AnalyticsFeaturePage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const analyticsFeatures = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Performance Tracking",
      description: "See your scores over time across all test types—chapter practice, simulations, and mock tests."
    },
    {
      icon: <PieChart className="w-6 h-6" />,
      title: "Chapter Breakdown",
      description: "Visualize your performance by chapter to identify which topics need more attention."
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Weak Area Detection",
      description: "Smart analysis automatically identifies your weakest topics for focused review based on your test history."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Progress Over Time",
      description: "Track your improvement from day one to see how far you've come in your preparation."
    }
  ];

  const metrics = [
    { label: "Overall Score", value: "85%", trend: "+12%", color: "text-green-600" },
    { label: "Tests Completed", value: "24", trend: "", color: "text-slate-900" },
    { label: "Questions Answered", value: "380", trend: "", color: "text-slate-900" },
    { label: "Weak Chapters", value: "2", trend: "-1", color: "text-green-600" }
  ];

  return (
    <>
      <SEO
        title="Analytics & Progress Tracking — Know Your Weak Areas"
        description="Track your Canadian citizenship test preparation progress with detailed analytics. See your scores by chapter, identify weak areas, and get personalized study recommendations. Focus your study time where it matters most."
        keywords="citizenship test progress tracking, weak areas citizenship test, citizenship test analytics, study progress tracker, Canadian citizenship test scores, personalized study plan"
        canonical="https://canadatestprep.ca/features/analytics"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Features" },
          { name: "Analytics", url: "/features/analytics" }
        ]}
      />
      <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-emerald-500/10 text-emerald-600 mb-4">Analytics & Insights</Badge>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-6">
                Track Your Progress with{" "}
                <span className="text-emerald-600">Smart Analytics</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Know exactly where you stand in your Canadian Citizenship Test preparation. 
                Our analytics dashboard shows your strengths, weaknesses, and progress over time—so you can study smarter, not harder.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8" data-testid="hero-cta">
                    See Your Analytics
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Real-Time Updates</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Smart Weak Area Detection</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Chapter Insights</span>
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Analytics Dashboard Mockup */}
              <Card className="bg-white border border-slate-200 shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-slate-900">Your Progress Dashboard</h3>
                    <Badge className="bg-emerald-100 text-emerald-600">Live</Badge>
                  </div>
                  
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {metrics.map((metric, index) => (
                      <div key={index} className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-500 mb-1">{metric.label}</p>
                        <div className="flex items-end gap-2">
                          <span className={`text-2xl font-bold ${metric.color}`}>{metric.value}</span>
                          {metric.trend && (
                            <span className="text-sm text-green-600 flex items-center">
                              <TrendingUp className="w-3 h-3 mr-1" />
                              {metric.trend}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Exam Readiness</span>
                      <span className="text-emerald-600 font-medium">85%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: "85%" }}></div>
                    </div>
                  </div>

                  {/* Chapter Performance Preview */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">History of Canada</span>
                      <span className="text-green-600">92%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Government Structure</span>
                      <span className="text-yellow-600">78%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Rights & Responsibilities</span>
                      <span className="text-red-500">65%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Analytics Features
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our analytics system gives you complete visibility into your preparation progress 
              and helps you focus your study time where it matters most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {analyticsFeatures.map((feature, index) => (
              <Card key={index} className="border border-slate-200 hover:border-emerald-300 transition-all" data-testid={`analytics-feature-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Weak Areas Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-orange-500/10 text-orange-600 mb-4">AI-Powered</Badge>
              <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
                Automatic Weak Area Detection
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Our AI analyzes your test results and identifies the chapters and topics where you're struggling. 
                Instead of wasting time on material you already know, focus on what will actually improve your score.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Analyzes patterns across all your test attempts</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Prioritizes topics by potential score improvement</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Updates in real-time as you complete more tests</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Links directly to practice questions for each weak area</span>
                </li>
              </ul>
            </div>
            <Card className="bg-white border border-slate-200 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Your Weak Areas
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">Rights & Responsibilities</span>
                      <Badge variant="destructive">65%</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">12 questions missed across 4 tests</p>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200">
                      Practice Now
                    </Button>
                  </div>
                  <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">Federal Elections</span>
                      <Badge className="bg-yellow-500">72%</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">8 questions missed across 3 tests</p>
                    <Button size="sm" variant="outline" className="text-yellow-600 border-yellow-200">
                      Practice Now
                    </Button>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">Canadian Symbols</span>
                      <Badge className="bg-green-500">91%</Badge>
                    </div>
                    <p className="text-sm text-slate-600">Strong performance! Keep it up.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              Why Analytics Matter for Canadian Citizenship Test Success
            </h2>
            <p className="text-slate-600 mb-4">
              Most people preparing for the Canadian Citizenship Test make a common mistake: 
              they study everything equally, spending just as much time on topics they already know 
              as on topics they struggle with. This is incredibly inefficient.
            </p>
            <p className="text-slate-600 mb-4">
              Our analytics system solves this problem by giving you data-driven insights into your preparation:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Overall readiness score:</strong> Know exactly how prepared you are for the real test</li>
              <li><strong>Chapter-by-chapter breakdown:</strong> See which of the 11 chapters need more work</li>
              <li><strong>Progress tracking:</strong> Watch your scores improve over time</li>
              <li><strong>Weak area detection:</strong> Let AI identify your problem areas automatically</li>
              <li><strong>Test history:</strong> Review all your past attempts and results</li>
            </ul>

            <h3 className="font-heading font-semibold text-xl text-slate-900 mt-8 mb-4">
              What's a Good Score Before Taking the Real Test?
            </h3>
            <p className="text-slate-600 mb-4">
              We recommend achieving these benchmarks before scheduling your official citizenship test:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Overall score:</strong> 85% or higher across all tests</li>
              <li><strong>Mock test scores:</strong> 17/20 or better on all three mock tests</li>
              <li><strong>Weak areas:</strong> No chapter below 75%</li>
              <li><strong>Consistency:</strong> Maintaining high scores across multiple test attempts</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Start Tracking Your Progress Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get instant access to our analytics dashboard and know exactly where you stand.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100 rounded-full px-10" data-testid="cta-final">
              Get Started Free
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
};

export default AnalyticsFeaturePage;



